<template>
  <app-custom-dialog
    :value="value"
    title="edit_recipe"
    :footer="true"
    width="780px"
    submitText="save"
    cancelText="cancel"
    @submit="save"
    @input="(val) => $emit('input', val)"
  >
    <div slot="activator">
      <!-- Variant being edited -->
      <div class="text-subtitle-1 font-weight-bold mb-4">
        {{ variantLabel }}
      </div>

      <!-- Add ingredient row -->
      <v-row align="start">
        <v-col cols="12" sm="5" class="pb-0">
          <TextSelect
            :key="`ingredient-${ingredientSelectKey}`"
            searchable
            :cache-items="false"
            label="ingredient"
            text="display_name"
            v-model="selectedIngredient"
            :items="ingredientOptions"
            :errorMessages="[]"
            :dense="true"
          />
          <div v-if="selectedIngredient" class="text-caption grey--text">
            {{ $t("unit") }}: {{ selectedIngredient.unit_name }} ·
            {{ $t("cost") }}:
            {{ selectedIngredient.unit_cost | NumberFormat }} /
            {{ selectedIngredient.unit_name }}
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="pb-0">
          <CurrencyInput
            label="usage_amount"
            v-model="usageAmount"
            :dense="true"
            :options="{ precision: 3 }"
          />
        </v-col>
        <v-col cols="6" sm="2" class="pb-0">
          <TextSelect
            label="unit"
            text="display_name"
            item-value="id"
            v-model="selectedUnit"
            :items="unitOptions"
            :errorMessages="[]"
            :dense="true"
            :disabled="!selectedIngredient"
          />
        </v-col>
        <v-col cols="12" sm="2" class="pb-0">
          <v-btn
            color="primary"
            depressed
            block
            class="mt-7"
            :disabled="!canAdd"
            @click="addItem"
          >
            <v-icon left size="16">fal fa-plus</v-icon>{{ $t("add") }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Recipe items -->
      <div class="text-subtitle-2 mt-4 mb-1">{{ $t("recipe_items") }}</div>
      <v-simple-table>
        <thead>
          <tr>
            <th scope="col" width="38%">{{ $t("ingredient") }}</th>
            <th scope="col" width="18%">{{ $t("usage_amount") }}</th>
            <th scope="col" width="14%">{{ $t("unit") }}</th>
            <th scope="col" width="20%">{{ $t("cost") }}</th>
            <th scope="col" width="10%">{{ $t("action") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in localItems" :key="idx">
            <td class="text-left">{{ ingredientLabel(item) }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.unit_name }}</td>
            <td>{{ lineCost(item) | NumberFormat }}</td>
            <td>
              <v-btn
                class="rounded-lg"
                fab
                depressed
                x-small
                @click="removeItem(idx)"
              >
                <v-icon x-small color="red">fal fa-trash</v-icon>
              </v-btn>
            </td>
          </tr>
          <tr v-if="!localItems.length">
            <td colspan="5" class="text-center grey--text py-4">
              {{ $t("no_recipe_items") }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <div class="d-flex justify-end align-center mt-3 font-weight-bold">
        <span class="mr-2">{{ $t("total_recipe_cost") }}:</span>
        <span class="primary--text text-h6">{{
          totalCost | NumberFormat
        }}</span>
      </div>
    </div>
  </app-custom-dialog>
</template>

<script>
import { myMixin } from "@/mixin/main";
import TextSelect from "@/components/global/TextSelect";
import CurrencyInput from "@/components/CurrencyInput";
import AppCustomDialog from "@/components/customDialog";
import { connectedUnitIds, recipeLineCost } from "@/utils/ingredientConversion";

export default {
  name: "VariantRecipeEditor",
  mixins: [myMixin],
  components: {
    TextSelect,
    CurrencyInput,
    AppCustomDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
    ingredients: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localItems: [],
      selectedIngredient: null,
      usageAmount: 0,
      selectedUnit: null,
    };
  },
  computed: {
    variantLabel() {
      const optionValues =
        this.variant.optionValues || this.variant.options || {};
      const label = Object.values(optionValues).join(" / ");
      return label || this.variant.sku || "";
    },
    ingredientOptions() {
      const selectedIngredientIds = new Set(
        this.localItems.map((item) => Number(item.ingredient_id))
      );

      return (this.ingredients || [])
        .filter(
          (ingredient) => !selectedIngredientIds.has(Number(ingredient.id))
        )
        .map((ingredient) => ({
          ...ingredient,
          display_name: this.ingredientLabel(ingredient),
        }));
    },
    ingredientSelectKey() {
      return this.localItems
        .map((item) => Number(item.ingredient_id))
        .sort((a, b) => a - b)
        .join(",");
    },
    unitOptions() {
      return this.unitsForIngredient(this.selectedIngredient);
    },
    canAdd() {
      return (
        !!this.selectedIngredient &&
        !!this.selectedUnit &&
        Number(this.usageAmount) > 0
      );
    },
    totalCost() {
      return this.localItems.reduce(
        (sum, item) => sum + this.lineCost(item),
        0
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          // Clone incoming items so Cancel discards edits
          this.localItems = JSON.parse(JSON.stringify(this.items || []));
          this.selectedIngredient = null;
          this.usageAmount = 0;
          this.selectedUnit = null;
        }
      },
    },
    selectedIngredient(ingredient) {
      this.usageAmount = 0;
      this.selectedUnit = this.unitsForIngredient(ingredient)[0] || null;
    },
  },
  methods: {
    ingredientLabel(item) {
      if (!item) return "";
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) {
        return item.name_cn;
      }
      return (
        item.name_la ||
        item.name_en ||
        item.name_th ||
        item.name_cn ||
        item.code ||
        "-"
      );
    },
    unitLabel(unit) {
      if (!unit) return "";
      if (this.$i18n.locale === "en" && unit.name_en) return unit.name_en;
      return unit.name || unit.name_en || "-";
    },
    unitsForIngredient(ingredient) {
      if (!ingredient || !ingredient.unit_id) return [];

      const baseUnitId = Number(ingredient.unit_id);
      const units = new Map();
      const baseUnit = {
        id: baseUnitId,
        name: ingredient.unit_name,
        name_en: ingredient.unit_name_en,
      };
      units.set(baseUnitId, {
        ...baseUnit,
        display_name: this.unitLabel(baseUnit),
      });

      const usageConversions = (ingredient.unit_conversions || []).filter(
        (conversion) =>
          conversion.purpose === "usage" && Number(conversion.is_active) === 1
      );

      usageConversions.forEach((conversion) => {
        if (!conversion.from_unit || !conversion.to_unit) {
          return;
        }

        [conversion.from_unit, conversion.to_unit].forEach((unit) => {
          const unitId = Number(unit.id);
          if (!unitId || units.has(unitId)) return;
          units.set(unitId, {
            ...unit,
            id: unitId,
            display_name: this.unitLabel(unit),
          });
        });
      });

      return connectedUnitIds(usageConversions, baseUnitId, ["usage"])
        .map((unitId) => units.get(unitId))
        .filter(Boolean);
    },
    lineCost(item) {
      return recipeLineCost(item, this.ingredients);
    },
    addItem() {
      if (!this.canAdd) return;
      const ing = this.selectedIngredient;
      const unit = this.selectedUnit;
      const quantity = Number(this.usageAmount);
      const existing = this.localItems.find(
        (i) => Number(i.ingredient_id) === Number(ing.id)
      );
      if (existing) {
        // Replace usage for an ingredient already in the recipe. line_cost came
        // from the API for the old quantity — drop it so the cost is recomputed
        // locally until the next save.
        Object.assign(existing, {
          quantity,
          unit_id: unit.id,
          unit_name: this.unitLabel(unit),
          unit_cost: ing.unit_cost,
          base_quantity: null,
          recipe_unit_cost: null,
          line_cost: null,
        });
      } else {
        this.localItems.push({
          ingredient_id: ing.id,
          quantity,
          unit_id: unit.id,
          unit_name: this.unitLabel(unit),
          unit_cost: ing.unit_cost,
          name_la: ing.name_la,
          name_en: ing.name_en,
          name_th: ing.name_th,
          name_cn: ing.name_cn,
        });
      }
      this.selectedIngredient = null;
      this.usageAmount = 0;
      this.selectedUnit = null;
    },
    removeItem(idx) {
      this.localItems.splice(idx, 1);
    },
    save() {
      this.$emit("save", JSON.parse(JSON.stringify(this.localItems)));
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped></style>
